import { useState, useCallback } from 'react';
import {
  xmLocalStorage,
  xmSessionStorage,
} from '@xmatters/common/utils/xmStorage';

// Slightly modified from https://gist.github.com/gragland/2970ae543df237a07be1dbbf810f23fe
const createUseStorage =
  storage =>
  (key, initialValue, opts = {}) => {
    const { validator } = opts;
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
      try {
        // Get from local storage by key
        const item = storage.getItem(key);
        if (item === null || item === undefined) {
          return initialValue;
        }

        const parsedItem = JSON.parse(item);
        const isValid = validator ? validator(parsedItem) : true;

        return isValid ? parsedItem : initialValue;
      } catch (error) {
        return initialValue;
      }
    });

    const setter = useCallback(
      newValue => {
        setStoredValue(newValue);
        try {
          if (newValue !== null && newValue !== undefined) {
            storage.setItem(key, JSON.stringify(newValue));
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            `Could not update localStorage with key: ${key} & value: ${newValue}. Error message: ${error.message} `,
          );
        }
      },
      [key, setStoredValue],
    );

    return [storedValue, setter];
  };

export const useLocalStorage = createUseStorage(xmLocalStorage);
export default useLocalStorage;
export const useSessionStorage = createUseStorage(xmSessionStorage);

/**
 * `useStickyLocalStorage` is an aspirational method to use when you would ideally like a key/value pair to
 * be stored on a server-side database, but are using localStorage as a quick way to get a feature complete,
 * or in cases where backend will not acept new data.
 *
 * At some point this _may_ get updated to sync contents with a backend database. Doing so will be completely
 * transparent to all who consume the method.
 *
 * Arguments should always be `key, initialValue, opts`, just like useLocalStorage.
 */
export const useStickyLocalStorage = (...args) => useLocalStorage(...args);
